import {
  Box,
  Spinner,
  Title,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import {
  A3InnuvaCompensationStepDocument,
  PayrollValueObjectsConceptCategoryEnum,
} from 'generated/resources/graphql'
import { z } from 'zod'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { DEFAULT_RESULT_CONCEPT } from 'modules/payroll_bundle/lib/constants'
import {
  A3InnunvaSource,
  A3InnuvaMapping,
  A3InnuvaWizardData,
} from 'modules/payroll_integrations_a3innuva/lib/types'

import { CompensationByLegalEntity } from './CompensationByLegalEntity'

const a3InnuvaCompensationSchema = z.object({
  mappings: z
    .array(
      z.object({
        factorial_id: z.string(),
        a3innuva_id: z.string(),
        meta: z.object({
          legal_entity_id: z.string(),
        }),
      })
    )
    .optional(),
})

const CompensationStep = ({ data, onDataChanged }: WizardStepRenderParams<A3InnuvaWizardData>) => {
  const compensations: A3InnunvaSource = data.sources?.compensations || {}
  useUnsafeEffect(() => {
    /**
     * This is a hack to be able to manage structured data.
     * Initially the data was thought to be a hash with both the key and the value as strings.
     * This data type is causing a lot of limitations and we are trying to improve it.
     * */
    if (data.sources?.compensations === undefined && data.answers.compensations !== undefined) {
      const parsedData = a3InnuvaCompensationSchema.safeParse(data.answers.compensations)
      onDataChanged({
        ...data,
        sources: {
          ...data.sources,
          compensations: parsedData.success ? parsedData.data : {},
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const session = useCurrentSession()
  const legalEntityMatcher = /^legal_entity_\d+$/
  const legalEntityKeys = Object.keys(data.answers).filter((key) => legalEntityMatcher.test(key))
  const legalEntityCodes = legalEntityKeys.map((key) => data.answers[key])

  const getCompanyCodes = (): string[] => {
    return [...new Set(legalEntityCodes.filter(Boolean))]
  }

  const payrollConceptCategories: PayrollValueObjectsConceptCategoryEnum[] = [
    'earnings_fixed_salary',
    'earnings_variable',
    'earnings_benefits_in_kind',
    'earnings_others',
    'deductions',
  ]

  const { loading, data: compensationsData } = useQuery(A3InnuvaCompensationStepDocument, {
    variables: {
      companyId: session.company.id,
      currentUserId: session.access.userId,
      companyCodes: getCompanyCodes(),
      categories: payrollConceptCategories,
      organizationalUnitId: data.answers.organizational_unit_id,
    },
  })

  const legalEntities = compensationsData?.companies.legalEntitiesConnection.nodes ?? []
  const a3innuvaCompensations =
    compensationsData?.payrollIntegrationsA3innuva.compensationCodesConnection.nodes ?? []
  const payrollConcepts = compensationsData?.compensations.conceptsConnection.nodes ?? []

  const activePayrollConcepts = payrollConcepts.filter(
    (concept) => concept.name !== DEFAULT_RESULT_CONCEPT.base_salary
  )

  const sortedActivePayrollConcepts = activePayrollConcepts.sort((a, b) =>
    a.label.localeCompare(b.label)
  )

  useOnNext(() => {
    trackEvent()
    onDataChanged({
      ...data,
      answers: {
        ...data.answers,
        compensations: JSON.parse(JSON.stringify(compensations)),
      },
    })
  })

  const trackEvent = () => {
    track(trackingEvents.payrollIntegrationsA3Innuva.a3innuvaSettingsCompensationSelected, {
      company_id: session.company.id,
      access_id: session.access.id,
      compensations: compensations?.mappings?.map((compensation) => compensation.a3innuva_id),
    })
  }

  const updateMappings = (newMappings: A3InnuvaMapping[]) => {
    onDataChanged({
      ...data,
      sources: {
        ...data.sources,
        compensations: {
          mappings: newMappings,
        },
      },
    })
  }

  return loading || compensations === undefined ? (
    <Spinner />
  ) : (
    <Box gap="s24" width="s600">
      <Title
        title={i18n.t(
          'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.compensation.title'
        )}
        description={i18n.t(
          'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.compensation.description'
        )}
      />
      <CompensationByLegalEntity
        data={data}
        onMappingChanged={updateMappings}
        a3innuvaCompensations={a3innuvaCompensations}
        legalEntities={legalEntities}
        payrollConcepts={sortedActivePayrollConcepts}
      />
    </Box>
  )
}

export const compensationStep = (): WizardStep<A3InnuvaWizardData> => ({
  title: i18n.t(
    'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.compensation.wizard_title'
  ),
  nextText: i18n.t('common.next'),
  render: (props) => <CompensationStep {...props} />,
  isCompleted: (_data) => true,
})
